import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  formatJakartaEndOfDay,
  formatJakartaStartOfDay,
} from "../../utils/date";
import {
  forceLogoutToLogin,
  isUnauthorizedApiResponse,
} from "../../utils/unauthorized";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";
const SUCCESS_CODES = new Set([200, 201, 204]);

const parseEnvelope = async (response) => {
  const isJson = response.headers
    .get("content-type")
    ?.toLowerCase()
    .includes("application/json");
  const payload = isJson ? await response.json() : null;

  if (isUnauthorizedApiResponse(response, payload)) {
    forceLogoutToLogin();
  }

  if (!payload || !SUCCESS_CODES.has(payload.resultCode)) {
    let message =
      payload?.resultDesc ||
      (response.ok ? "Unexpected response from server." : response.statusText);

    if (payload?.errors) {
      const errorMessages = Object.values(payload.errors)
        .flat()
        .filter(Boolean);
      if (errorMessages.length) {
        message = errorMessages.join(", ");
      }
    }

    const error = new Error(message);
    error.payload = payload;
    throw error;
  }

  if (payload.data !== undefined) {
    return payload.data;
  }

  return payload;
};

const getAuthHeaders = (state) => {
  const token = state.auth?.accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildJobsQuery = (state) => {
  const { filters, pagination, sort } = state.ticketUploads;
  const params = new URLSearchParams();
  params.append("page", pagination.page + 1);
  params.append("pageSize", pagination.pageSize);
  if (filters.status && filters.status !== "ALL") {
    params.append("status", filters.status);
  }
  if (filters.eventId) {
    params.append("eventId", filters.eventId);
  }
  if (filters.startDate) {
    params.append("startDate", formatJakartaStartOfDay(filters.startDate));
  }
  if (filters.endDate) {
    params.append("endDate", formatJakartaEndOfDay(filters.endDate));
  }
  if (sort?.field) {
    params.append("sortBy", sort.field);
    params.append("sortDirection", sort.direction || "desc");
  }
  return params.toString();
};

export const fetchTicketJobs = createAsyncThunk(
  "ticketUploads/fetchJobs",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const query = buildJobsQuery(state);
      const response = await fetch(
        `${API_BASE_URL}/ticket-upload-jobs?${query}`,
        {
          headers: {
            Accept: "application/json",
            ...getAuthHeaders(state),
          },
        }
      );
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch upload jobs.");
    }
  }
);

export const fetchTicketEvents = createAsyncThunk(
  "ticketUploads/fetchEvents",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(`${API_BASE_URL}/events?all=true&sortBy=created_datetime&sortDirection=desc`, {
        headers: {
          Accept: "application/json",
          ...getAuthHeaders(state),
        },
      });
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch events.");
    }
  }
);

export const fetchTicketJobDetail = createAsyncThunk(
  "ticketUploads/fetchDetail",
  async (jobId, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(
        `${API_BASE_URL}/ticket-upload-jobs/${jobId}`,
        {
          headers: {
            Accept: "application/json",
            ...getAuthHeaders(state),
          },
        }
      );
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch job detail.");
    }
  }
);

export const createTicketUploadJob = createAsyncThunk(
  "ticketUploads/createJob",
  async ({ eventId, file }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const formData = new FormData();
      formData.append("event_id", eventId);
      formData.append("file", file);

      const response = await fetch(`${API_BASE_URL}/ticket-upload-jobs`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(state),
        },
        body: formData,
      });

      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to upload tickets.");
    }
  }
);

export const rerunTicketUploadJob = createAsyncThunk(
  "ticketUploads/rerunJob",
  async (jobId, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(
        `${API_BASE_URL}/ticket-upload-jobs/${jobId}/rerun`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(state),
          },
        }
      );

      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to rerun job.");
    }
  }
);

const initialFilters = {
  status: "ALL",
  eventId: "",
  startDate: null,
  endDate: null,
};

const initialMeta = {
  page: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: 0,
};

const initialState = {
  jobs: [],
  meta: initialMeta,
  filters: initialFilters,
  pagination: {
    page: 0,
    pageSize: 10,
  },
  sort: {
    field: "created_datetime",
    direction: "desc",
  },
  loading: false,
  error: null,
  events: [],
  eventsStatus: "idle",
  eventsError: null,
  detail: {
    job: null,
    loading: false,
    error: null,
  },
  createStatus: "idle",
  createError: null,
  rerunStatus: {},
};

const ticketUploadsSlice = createSlice({
  name: "ticketUploads",
  initialState,
  reducers: {
    setStatusFilter: (state, action) => {
      state.filters.status = action.payload;
      state.pagination.page = 0;
    },
    setEventFilter: (state, action) => {
      state.filters.eventId = action.payload;
      state.pagination.page = 0;
    },
    setDateRangeFilter: (state, action) => {
      state.filters.startDate = action.payload.startDate || null;
      state.filters.endDate = action.payload.endDate || null;
      state.pagination.page = 0;
    },
    setTicketPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setTicketPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
      state.pagination.page = 0;
    },
    setTicketUploadsSort: (state, action) => {
      const { field, direction } = action.payload || {};
      if (!field) return;
      state.sort = {
        field,
        direction: direction === "asc" ? "asc" : "desc",
      };
      state.pagination.page = 0;
    },
    resetCreateForm: (state) => {
      state.createStatus = "idle";
      state.createError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTicketJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketJobs.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        state.jobs = payload.items || payload.data?.items || [];
        state.meta = {
          ...initialMeta,
          ...(payload.meta || payload.data?.meta || {}),
        };
      })
      .addCase(fetchTicketJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch upload jobs.";
      })
      .addCase(fetchTicketEvents.pending, (state) => {
        state.eventsStatus = "loading";
        state.eventsError = null;
      })
      .addCase(fetchTicketEvents.fulfilled, (state, action) => {
        state.eventsStatus = "succeeded";
        const payload = action.payload;
        const list =
          payload?.items ||
          payload?.data?.items ||
          payload?.data ||
          payload ||
          [];
        state.events = Array.isArray(list) ? list : [];
      })
      .addCase(fetchTicketEvents.rejected, (state, action) => {
        state.eventsStatus = "failed";
        state.eventsError = action.payload || "Failed to load events.";
      })
      .addCase(fetchTicketJobDetail.pending, (state) => {
        state.detail.loading = true;
        state.detail.error = null;
      })
      .addCase(fetchTicketJobDetail.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.job = action.payload;
      })
      .addCase(fetchTicketJobDetail.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.error = action.payload || "Failed to load job detail.";
      })
      .addCase(createTicketUploadJob.pending, (state) => {
        state.createStatus = "loading";
        state.createError = null;
      })
      .addCase(createTicketUploadJob.fulfilled, (state) => {
        state.createStatus = "succeeded";
      })
      .addCase(createTicketUploadJob.rejected, (state, action) => {
        state.createStatus = "failed";
        state.createError = action.payload || "Failed to upload tickets.";
      })
      .addCase(rerunTicketUploadJob.pending, (state, action) => {
        state.rerunStatus[action.meta.arg] = "loading";
      })
      .addCase(rerunTicketUploadJob.fulfilled, (state, action) => {
        const job = action.payload;
        if (job?.id) {
          state.jobs = state.jobs.map((existing) =>
            existing.id === job.id ? { ...existing, ...job } : existing
          );
        }
        state.rerunStatus[job?.id || action.meta.arg] = "idle";
      })
      .addCase(rerunTicketUploadJob.rejected, (state, action) => {
        state.rerunStatus[action.meta.arg] = "failed";
        state.error = action.payload || "Failed to rerun job.";
      });
  },
});

export const {
  setStatusFilter,
  setEventFilter,
  setDateRangeFilter,
  setTicketPage,
  setTicketPageSize,
  setTicketUploadsSort,
  resetCreateForm,
} = ticketUploadsSlice.actions;

export const selectTicketUploadsFilters = (state) => state.ticketUploads.filters;
export const selectTicketUploadsPagination = (state) =>
  state.ticketUploads.pagination;
export const selectTicketUploadsSort = (state) => state.ticketUploads.sort;
export const selectTicketJobs = (state) => state.ticketUploads.jobs;
export const selectTicketUploadsMeta = (state) => state.ticketUploads.meta;
export const selectTicketUploadsLoading = (state) => state.ticketUploads.loading;
export const selectTicketUploadsError = (state) => state.ticketUploads.error;
export const selectTicketUploadsEvents = (state) => state.ticketUploads.events;
export const selectTicketUploadsEventsStatus = (state) =>
  state.ticketUploads.eventsStatus;
export const selectTicketUploadsEventsError = (state) =>
  state.ticketUploads.eventsError;
export const selectTicketUploadsCreateStatus = (state) =>
  state.ticketUploads.createStatus;
export const selectTicketUploadsCreateError = (state) =>
  state.ticketUploads.createError;
export const selectTicketUploadDetailState = (state) =>
  state.ticketUploads.detail;
export const selectTicketUploadRerunStatus = (state) =>
  state.ticketUploads.rerunStatus;

export default ticketUploadsSlice.reducer;
